import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import API_URL from '../../config/api';

const supabase = createClient('https://cvytfjkefrrfibbbstig.supabase.co', process.env.REACT_APP_SUPABASE_ANON_KEY || 'sb_secret_CqiRDhHfw4Rg2e9ECdgmzA_mB85NFhR');

const formatBRL = (value) => {
  const num = typeof value === 'number' && isFinite(value) ? value : 0;
  return `R$ ${num.toFixed(2).replace('.', ',')}`;
};

const AprovarPedidos = () => {
  const [pedidosPendentes, setPedidosPendentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchPedidosPendentes();
  }, []);

  const fetchAttachments = async (quotationId) => {
    try {
      const response = await fetch(`${API_URL}/api/attachments/${quotationId}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar anexos');
      }
      const result = await response.json();
      setAttachments(result.attachments || []);
    } catch (error) {
      console.error('Erro ao buscar anexos:', error);
      setAttachments([]);
    }
  };

  const fetchPedidosPendentes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/quotations?status=Pendente Aprovação`);
      if (!response.ok) {
        throw new Error('Erro ao buscar pedidos pendentes');
      }
      const result = await response.json();
      const allPedidos = result.quotations || [];
      
      // Agrupar por quotation_id e pegar apenas a versão mais recente
      const latestVersions = {};
      allPedidos.forEach(p => {
        const id = p.quotation_id;
        if (!latestVersions[id] || p.version > latestVersions[id].version) {
          latestVersions[id] = p;
        }
      });
      
      setPedidosPendentes(Object.values(latestVersions));
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const totalItems = pedidosPendentes.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = pedidosPendentes.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleAprovar = async (pedido) => {
    if (!window.confirm(`Deseja aprovar o pedido ${pedido.quotation_id}.${pedido.version}?`)) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(
        `${API_URL}/api/quotations/${pedido.quotation_id}/${pedido.version}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'Importado' }),
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao aprovar pedido');
      }

      alert('Pedido aprovado com sucesso!');
      await fetchPedidosPendentes();
      
    } catch (error) {
      console.error('Erro ao aprovar:', error);
      alert('Erro ao aprovar pedido: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRejeitar = async (pedido) => {
    if (!window.confirm(`Deseja rejeitar o pedido ${pedido.quotation_id}.${pedido.version}? Ele voltará para "Em Negociação".`)) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await fetch(
        `${API_URL}/api/quotations/${pedido.quotation_id}/${pedido.version}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: 'Em Negociação' }),
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao rejeitar pedido');
      }

      alert('Pedido rejeitado. Status alterado para "Em Negociação".');
      await fetchPedidosPendentes();
      
    } catch (error) {
      console.error('Erro ao rejeitar:', error);
      alert('Erro ao rejeitar pedido: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (pedido) => {
    setSelectedPedido(pedido);
    setIsViewModalOpen(true);
    await fetchAttachments(pedido.quotation_id);
  };

  return (
    <div className="p-6 bg-white rounded shadow">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Aprovar Pedidos</h1>
        <button
          onClick={fetchPedidosPendentes}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Atualizar</span>
        </button>
      </div>

      {loading ? (
        <p>Carregando pedidos pendentes...</p>
      ) : pedidosPendentes.length === 0 ? (
        <div className="text-center py-12">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500 text-lg">Nenhum pedido pendente de aprovação</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Número</th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm hidden md:table-cell">Filial</th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Cliente</th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm hidden lg:table-cell">Vendedor</th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Valor</th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm hidden sm:table-cell">Data</th>
                <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Ações</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((pedido) => (
                <tr key={`${pedido.quotation_id}-${pedido.version}`} className="hover:bg-gray-50">
                  <td className="border px-2 sm:px-4 py-2 text-center font-semibold text-xs sm:text-sm whitespace-nowrap">
                    {pedido.quotation_id}.{pedido.version}
                  </td>
                  <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden md:table-cell">{pedido.data?.filial}</td>
                  <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm">{pedido.data?.cliente}</td>
                  <td className="border px-2 sm:px-4 py-2 text-xs sm:text-sm hidden lg:table-cell">{pedido.data?.vendedor}</td>
                  <td className="border px-2 sm:px-4 py-2 text-right font-semibold text-xs sm:text-sm whitespace-nowrap">
                    {(() => {
                      const itemsTotal = pedido.data?.items ? pedido.data.items.reduce((sum, item) => sum + (item.valorTotal || 0), 0) : 0;
                      const freightToAdd = pedido.data?.tipoFrete === 'CIF' ? (pedido.data.freteDestacadoCalculado || 0) : 0;

                      let descontoAmount = 0;
                      if (pedido.data?.desconto && pedido.data.desconto > 0) {
                        if (pedido.data.descontoType === '%') {
                          const totalOver = pedido.data?.items ? pedido.data.items.reduce((sum, item) => {
                            const valorTabela = item.valorTabela || 0;
                            const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                            return sum + over * item.quantidade;
                          }, 0) : 0;
                          descontoAmount = totalOver * (parseFloat(pedido.data.desconto) / 100);
                        } else {
                          descontoAmount = parseFloat(pedido.data.desconto) || 0;
                        }
                      }

                      const valorTotalComDesconto = itemsTotal + freightToAdd - descontoAmount;
                      return formatBRL(valorTotalComDesconto);
                    })()}
                  </td>
                  <td className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm hidden sm:table-cell whitespace-nowrap">
                    {new Date(pedido.created_at).toLocaleDateString()}
                  </td>
                  <td className="border px-2 sm:px-4 py-2">
                    <div className="flex flex-col sm:flex-row justify-center gap-1 sm:gap-2">
                      <button
                        onClick={() => handleView(pedido)}
                        className="px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs sm:text-sm rounded hover:bg-blue-700 whitespace-nowrap"
                        title="Visualizar"
                      >
                        Ver
                      </button>
                      <button
                        onClick={() => handleAprovar(pedido)}
                        className="px-2 sm:px-3 py-1 bg-green-600 text-white text-xs sm:text-sm rounded hover:bg-green-700 whitespace-nowrap"
                        title="Aprovar pedido"
                      >
                        Aprovar
                      </button>
                      <button
                        onClick={() => handleRejeitar(pedido)}
                        className="px-2 sm:px-3 py-1 bg-red-600 text-white text-xs sm:text-sm rounded hover:bg-red-700 whitespace-nowrap"
                        title="Rejeitar pedido"
                      >
                        Rejeitar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Visualização */}
      {isViewModalOpen && selectedPedido && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded shadow-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto relative">
            <h2 className="text-2xl font-bold mb-6 border-b pb-2">
              Detalhes do Pedido {selectedPedido.quotation_id}.{selectedPedido.version}
            </h2>
            <button
              onClick={() => setIsViewModalOpen(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
              aria-label="Fechar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {selectedPedido.data && (
              <div className="space-y-6">
                <div className="bg-gray-100 p-4 rounded-md space-y-4">
                  {/* Informações do Orçamento */}
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg mb-2">Informações do Pedido</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Vendedor</p>
                        <p className="font-medium">{selectedPedido.data.vendedor}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Filial</p>
                        <p className="font-medium">{selectedPedido.data.filial}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Cliente</p>
                        <p className="font-medium">{selectedPedido.data.cliente}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Comprador</p>
                        <p className="font-medium">{selectedPedido.data.comprador}</p>
                      </div>
                    </div>
                  </div>

                  {/* Itens do Pedido */}
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg mb-2">Itens do Pedido</h3>
                    <div className="overflow-x-auto -mx-4 sm:mx-0">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                            <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qtd</th>
                            <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Valor Unit.</th>
                            <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedPedido.data.items?.map((item, idx) => (
                            <tr key={idx}>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm">{item.produto}</td>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm whitespace-nowrap">{item.quantidade?.toFixed(3).replace('.', ',')}</td>
                              <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm hidden sm:table-cell whitespace-nowrap">
                                {formatBRL((() => {
                                  const valorTabela = item.valorTabela || 0;
                                  const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                                  return valorTabela + over;
                                })())}
                              </td>
                              <td className="px-2 sm:px-4 py-2 font-semibold text-xs sm:text-sm whitespace-nowrap">{formatBRL(item.valorTotal || 0)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totais */}
                  <div className="bg-white p-4 rounded border">
                    {(() => {
                      const itemsTotal = selectedPedido.data.items?.reduce((sum, item) => sum + (item.valorTotal || 0), 0) || 0;
                      const freightToAdd = selectedPedido.data?.tipoFrete === 'CIF' ? (selectedPedido.data.freteDestacadoCalculado || 0) : 0;

                      let descontoAmount = 0;
                      if (selectedPedido.data?.desconto && selectedPedido.data.desconto > 0) {
                        if (selectedPedido.data.descontoType === '%') {
                          const totalOver = selectedPedido.data?.items ? selectedPedido.data.items.reduce((sum, item) => {
                            const valorTabela = item.valorTabela || 0;
                            const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                            return sum + over * item.quantidade;
                          }, 0) : 0;
                          descontoAmount = totalOver * (parseFloat(selectedPedido.data.desconto) / 100);
                        } else {
                          descontoAmount = parseFloat(selectedPedido.data.desconto) || 0;
                        }
                      }

                      const valorTotalComDesconto = itemsTotal + freightToAdd - descontoAmount;

                      const totalOver = selectedPedido.data?.items ? selectedPedido.data.items.reduce((sum, item) => {
                        const valorTabela = item.valorTabela || 0;
                        const over = item.over1Type === '%' ? valorTabela * (item.over1 || 0) / 100 : (item.over1 || 0);
                        return sum + over * item.quantidade;
                      }, 0) : 0;

                      const ajusteFrete = parseFloat(selectedPedido.data?.ajusteFrete || 0);
                      const adjustedTotalOver = totalOver + ajusteFrete - descontoAmount;

                      return (
                        <>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-700">Total de Itens:</span>
                            <span className="font-semibold">
                              {formatBRL(itemsTotal)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-700">Total Over:</span>
                            <span className="font-semibold text-green-600">
                              {formatBRL(adjustedTotalOver)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-700">Frete:</span>
                            <span className="font-semibold">
                              {formatBRL(freightToAdd)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-700">Desconto:</span>
                            <span className="font-semibold text-red-600">
                              {formatBRL(-descontoAmount)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t-2">
                            <span className="text-lg font-bold">Total do Pedido:</span>
                            <span className="text-lg font-bold text-green-600">
                              {formatBRL(valorTotalComDesconto)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Anexos do Pedido */}
                  <div>
                    <h3 className="font-semibold text-base sm:text-lg mb-2">Anexos do Pedido</h3>
                    {attachments.length === 0 ? (
                      <p className="text-gray-500 italic text-sm">Nenhum anexo encontrado</p>
                    ) : (
                      <div className="bg-white rounded border overflow-x-auto -mx-4 sm:mx-0">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                              <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Descrição</th>
                              <th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Data</th>
                              <th className="px-2 sm:px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {attachments.map((att, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-2 sm:px-4 py-2">
                                  <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                                    att.tipo_arquivo === 'Pedido Assinado ou Ordem de Compra' 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {att.tipo_arquivo}
                                  </span>
                                </td>
                                <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm hidden sm:table-cell">{att.descricao}</td>
                                <td className="px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-600 hidden md:table-cell whitespace-nowrap">
                                  {new Date(att.updated_at).toLocaleDateString('pt-BR')}
                                </td>
                                <td className="px-2 sm:px-4 py-2 text-center">
                                  <button
                                    onClick={() => window.open(att.publicURL, '_blank')}
                                    className="px-2 sm:px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 whitespace-nowrap"
                                  >
                                    Abrir
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                    <button
                      onClick={() => setIsViewModalOpen(false)}
                      className="px-6 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      Fechar
                    </button>
                    <button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleRejeitar(selectedPedido);
                      }}
                      className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Rejeitar
                    </button>
                    <button
                      onClick={() => {
                        setIsViewModalOpen(false);
                        handleAprovar(selectedPedido);
                      }}
                      className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Aprovar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AprovarPedidos;
